import React, { FC, useState, useEffect, useMemo } from 'react';
import { Options } from 'react-select';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import join from 'lodash/join';
import qs from 'query-string';

import { IDynamicUiField, IDictionaryValue } from 'plugins/api/types';

import { reduxActions, useDispatch } from 'store';
import { FormElement, Modal } from 'components/controls';
import DynamicListTable from 'plugins/components/controls/DynamicList/components/DynamicListTable';
import { useLoadData } from 'plugins/api/endpoints';
import { loadDataUrlParser } from 'plugins/util/parser';
import WiseSelect from 'components/wise/WiseSelect';

import 'plugins/theme/components/EditPanel/EditPanel.scss';

// typ danych wejściowych
interface IProps {
  field: IDynamicUiField;
  pageSymbol: string;
  componentSymbol: string;
  currentValue?: string | null;
}

const DynamicList: FC<IProps> = ({ field, pageSymbol, componentSymbol, currentValue }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { search } = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  // ustawianie wartości - dla multichoice tablica, dla singlechoice pojedyncza wartość
  const [value, setValue] = useState<IDictionaryValue[] | IDictionaryValue | null>(
    field.dictionary?.multi_choice ? [] : null
  );
  // parametry zapytania
  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 10,
    searchKeyword: ''
  });

  // Sprawdzenie czy finalny URL zawiera wartości null po parsowaniu (przed wykonaniem zapytania API)
  const isValidApiUrl = useMemo(() => {
    if (!field.dictionary?.values_get_u_i_api) return false;

    // Symulacja tego co useLoadData robi wewnętrznie - używa loadDataUrlParser z window.location.search
    const urlSearchParams = qs.parse(search);
    const parsedUrl = loadDataUrlParser(field.dictionary.values_get_u_i_api, urlSearchParams);

    // Sprawdzenie czy sparsowany URL zawiera null lub undefined w parametrach zapytania
    if (
      parsedUrl &&
      (parsedUrl.includes('=null') ||
        parsedUrl.includes('=undefined') ||
        parsedUrl.includes('?null') ||
        parsedUrl.includes('&null'))
    ) {
      return false;
    }

    return true;
  }, [field.dictionary?.values_get_u_i_api, search]);

  const effectiveValue = currentValue !== undefined ? currentValue : (field.value as string);

  const { data: dictionaryFromUIData } = useLoadData(
    field.dictionary.values_get_u_i_api || '',
    effectiveValue && effectiveValue !== '0' && effectiveValue !== ''
      ? { value: effectiveValue }
      : undefined,
    {
      enabled: isValidApiUrl && !!field.dictionary?.values_get_u_i_api
    }
  );

  const options = useMemo(
    () =>
      (dictionaryFromUIData?.items || []).map((item) => ({
        value: item.value,
        label: item.text,
        item: item
      })),
    [dictionaryFromUIData?.items]
  );

  const onClose = () => {
    setIsOpen(false);

    setQueryParams({
      page: 1,
      limit: 10,
      searchKeyword: ''
    });
  };

  const onChangeHandler = (item: any) => {
    let fieldValue: string | number | null;

    if (field.dictionary?.multi_choice) {
      // Multichoice: dodawanie opcji dla każdego elementu z tablicy
      if (Array.isArray(item) && item.length > 0) {
        item.forEach((singleItem: any) => {
          if (singleItem && !options.find((option) => option.value === singleItem.value)) {
            options.push({
              value: singleItem.value,
              label: singleItem.text,
              item: singleItem
            });
          }
        });
        fieldValue = join(
          item.map((o: any) => o.value.toString()),
         '|'
        );
      } else {
        fieldValue = '';
      }
      setValue(item || []);
    } else {
      // Singlechoice: dodawanie opcji dla pojedynczego elementu
      if (item && !options.find((option) => option.value === item.value)) {
        options.push({
          value: item.value,
          label: item.text,
          item
        });
      }
      // Singlechoice: pojedyncza wartość
      fieldValue = item?.value || null;
      setValue(item || null);
    }

    // sub_dictionary: przy zmianie głównej wartości wyczyść wartość sub-pola w store
    if (field.dictionary?.sub_dictionary) {
      dispatch(
        reduxActions.setFieldValue({
          pageSymbol,
          componentSymbol,
          fieldName: field.dictionary.sub_dictionary.field_symbol,
          fieldValue: null
        })
      );
    }

    dispatch(
      reduxActions.setFieldValue({
        pageSymbol,
        componentSymbol,
        fieldName: field.field_symbol,
        fieldValue
      })
    );

    onClose();
  };

  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keyup', handleEscape);

      return;
    } else {
      document.removeEventListener('keyup', handleEscape);
    }

    return () => window.removeEventListener('keyup', handleEscape);
  }, [isOpen]);

  useEffect(() => {
    if (!dictionaryFromUIData?.items) return;

    if (field.dictionary?.multi_choice) {
      // Multichoice: parsowanie wartości z separatorem
      const separator = '|';
      const valueArray = (effectiveValue as string)?.split(separator).filter(Boolean) || [];
      const results = filter(dictionaryFromUIData.items, (item) => {
        return includes(valueArray, item.value?.toString());
      });
      setValue(results);
    } else {
      // Singlechoice: pojedyncza wartość
      const selectedValue = dictionaryFromUIData.items.find(
        (item) => item.value?.toString() === effectiveValue?.toString()
      );
      setValue(selectedValue || null);
    }
  }, [dictionaryFromUIData?.items, effectiveValue]);

  // Type guard do sprawdzania czy wartość to string lub number
  const isValidValue = (val: string | number | null | undefined | any): val is string | number => {
    return val !== null && val !== undefined && typeof val !== 'object';
  };

  const selectedValues = useMemo(() => {
    if (field.dictionary?.multi_choice) {
      if (Array.isArray(value)) {
        return value
          .map((el) => el.value)
          .filter(isValidValue);
      }
      return [];
    } else {
      if (value && !Array.isArray(value)) {
        return isValidValue(value.value) ? value.value : null;
      }
      return null;
    }
  }, [value, field.dictionary?.multi_choice]);

  return (
    <div style={{marginTop: -18}}>
      <FormElement>
        {field.dictionary?.multi_choice ? (
          <WiseSelect
            label={field.label}
            placeholder={t('Wybierz')}
            value={selectedValues as (string | number)[] | string | undefined}
            options={options}
            displayVariant="modal"
            mode="multichoice"
            disabled={!field.editable}
            styleGroup={'trader-panel-form'}
            clearable={field.nullable}
            onOpenChange={(isOpen) => {
              if (isOpen && field.editable) {
                setIsOpen(true);
              }
            }}
            onChange={(items: Options<any>) => {
              if (Array.isArray(items)) {
                const selectedItems = items
                  .map((option) => option.item)
                  .filter(Boolean);
                onChangeHandler(selectedItems);
              } else {
                onChangeHandler([]);
              }
            }}
          />
        ) : (
          <WiseSelect
            label={field.label}
            placeholder={t('Wybierz')}
            value={selectedValues as string | number | null | undefined}
            options={options}
            displayVariant="modal"
            styleGroup={'trader-panel-form'}
            mode="singlechoice"
            disabled={!field.editable}
            clearable={field.nullable}
            onOpenChange={(isOpen) => {
              if (isOpen && field.editable) {
                setIsOpen(true);
              }
            }}
            onChange={(item: any) => {
              if (item) {
                onChangeHandler(item);
              } else {
                onChangeHandler(null);
              }
            }}
          />
        )}
        {field.hint && <small>{field.hint}</small>}
      </FormElement>

      {isOpen && field.editable && (
        <Modal title="" onClose={() => onClose()}>
          <DynamicListTable
            field={field}
            setIsOpen={setIsOpen}
            queryParams={queryParams}
            setQueryParams={setQueryParams}
            onClose={onClose}
            onChangeHandler={onChangeHandler}
          />
        </Modal>
      )}
    </div>
  );
};

export default DynamicList;
